import { useState } from "react";
import { ChevronLeft, ChevronRight, CalendarDays, X } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { useI18n } from "@/lib/i18n";

export const WINDOWS = [["morning", "8–12"], ["afternoon", "12–16"], ["evening", "16–19"]];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fromIso = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const today = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };

export function slotLabel(s, lang, t) {
  const w = WINDOWS.find((x) => x[0] === s.window);
  return `${fromIso(s.date).toLocaleDateString(lang, { weekday: "short", month: "short", day: "numeric" })} · ${t(`cal.${s.window}`)} (${w?.[1]})`;
}

export default function AvailabilityPicker({ slots, onChange }) {
  const { t, lang } = useI18n();
  const [offset, setOffset] = useState(0);
  const [month, setMonth] = useState(false);
  const [day, setDay] = useState(iso(today()));
  const start = today();
  start.setDate(start.getDate() + offset * 7);
  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  const has = (date, w) => slots.some((s) => s.date === date && s.window === w);
  const toggle = (w) => onChange(has(day, w) ? slots.filter((s) => !(s.date === day && s.window === w)) : [...slots, { date: day, window: w }]);
  const pick = (d) => { const k = iso(d); setDay(k); setOffset(Math.max(0, Math.floor((d - today()) / 864e5 / 7))); };

  return (
    <div className="space-y-4" data-testid="availability-picker">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-slate-600">{t("cal.pick")}</p>
        <button type="button" onClick={() => setMonth(!month)} className="inline-flex items-center gap-1 text-sm font-semibold text-teal-700 hover:underline shrink-0" data-testid="cal-toggle-month">
          <CalendarDays className="h-4 w-4" />{month ? t("cal.week_view") : t("cal.month")}
        </button>
      </div>
      {month ? (
        <div className="flex justify-center rounded-2xl border border-slate-200">
          <Calendar mode="single" selected={fromIso(day)} onSelect={(d) => d && pick(d)} disabled={{ before: today() }} data-testid="cal-month" />
        </div>
      ) : (
        <div className="flex items-center gap-1">
          <button type="button" disabled={offset === 0} onClick={() => setOffset(offset - 1)} className="p-1.5 rounded-full hover:bg-slate-100 disabled:opacity-30" data-testid="cal-prev-week"><ChevronLeft className="h-4 w-4 rtl:rotate-180" /></button>
          <div className="grid grid-cols-7 gap-1 flex-1">
            {days.map((d) => {
              const k = iso(d);
              const n = slots.filter((s) => s.date === k).length;
              return (
                <button type="button" key={k} onClick={() => setDay(k)} data-testid={`cal-day-${k}`}
                  className={`relative rounded-xl py-2 text-center border transition-colors ${day === k ? "bg-teal-600 text-white border-teal-600" : "bg-white border-slate-200 hover:border-teal-400"}`}>
                  <span className="block text-[10px] uppercase tracking-wide opacity-80">{d.toLocaleDateString(lang, { weekday: "short" })}</span>
                  <span className="block text-base font-semibold">{d.getDate()}</span>
                  {n > 0 && <span className="absolute top-1 end-1 h-1.5 w-1.5 rounded-full bg-amber-400" />}
                </button>
              );
            })}
          </div>
          <button type="button" onClick={() => setOffset(offset + 1)} className="p-1.5 rounded-full hover:bg-slate-100" data-testid="cal-next-week"><ChevronRight className="h-4 w-4 rtl:rotate-180" /></button>
        </div>
      )}
      <div>
        <p className="text-xs font-semibold text-slate-500 mb-2">{fromIso(day).toLocaleDateString(lang, { weekday: "long", month: "long", day: "numeric" })}</p>
        <div className="grid grid-cols-3 gap-2">
          {WINDOWS.map(([w, h]) => (
            <button type="button" key={w} onClick={() => toggle(w)} data-testid={`cal-slot-${w}`}
              className={`rounded-xl border px-2 py-2.5 text-sm transition-colors ${has(day, w) ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 hover:border-slate-500"}`}>
              <span className="block font-semibold">{t(`cal.${w}`)}</span><span className="block text-xs opacity-70">{h}</span>
            </button>
          ))}
        </div>
      </div>
      {slots.length > 0 && (
        <div data-testid="cal-selected">
          <p className="text-xs font-semibold text-slate-500 mb-2">{t("cal.selected")}</p>
          <div className="flex flex-wrap gap-2">
            {slots.map((s) => (
              <span key={s.date + s.window} className="inline-flex items-center gap-1 rounded-full bg-teal-50 text-teal-800 border border-teal-200 px-3 py-1 text-xs">
                {slotLabel(s, lang, t)}
                <button type="button" onClick={() => onChange(slots.filter((x) => x !== s))} aria-label="remove"><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
